import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Gauge, Loader2 } from 'lucide-react';

import { TARGET_SCORE } from '@/data/localPotential';
import { useShopPath } from '@/hooks/useShopPath';
import { useLocalPotential } from '@/hooks/useLocalPotential';
import { ComingSoonOverlay } from '@/components/ui/coming-soon-overlay';

const prefersReducedMotion = () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/**
 * Compact Local Potential gauge for the Overview header — small ring + copy +
 * "Improve my store" CTA, with the TARGET_SCORE rendered as a tick mark.
 *
 * Self-fetching (NEA-5869): reads the live score from `useLocalPotential`. While
 * the score is loading it shows a spinner (not a misleading 0%); once it lands,
 * the ring grows in from 0 (CSS transition) and the number counts up to match —
 * rather than jumping. In-session bumps (e.g. enabling a channel) animate from
 * the current value to the new one. Everything respects `prefers-reduced-motion`,
 * and a fallback guarantees the final value even where requestAnimationFrame is
 * throttled (e.g. a background tab).
 */
export function LocalPotentialHeader({ subtitle, comingSoon = false, compact = false, shopId, animate = false }) {
	const { shopPath } = useShopPath();
	const { data } = useLocalPotential(shopId);
	const score = data?.score ?? null;
	const loading = score == null;

	const size = compact ? 72 : 76;
	const stroke = compact ? 8 : 9;
	const r = (size - stroke) / 2;
	const c = 2 * Math.PI * r;

	const [ringPct, setRingPct] = useState(0);
	const [displayValue, setDisplayValue] = useState(0);
	const fromRef = useRef(0);

	useEffect(() => {
		if (score == null) return undefined;
		// Only the Overview gauge animates: PageScore (other page headers) renders the
		// score statically, so the count-up + ring grow-in run on the Overview alone (NEA-5935).
		if (!animate) return undefined;
		const start = fromRef.current;
		fromRef.current = score;

		// Defer the ring change a tick so its CSS transition grows it in from the
		// previous length, rather than the circle rendering at the final length.
		const ringTimer = setTimeout(() => setRingPct(score / 100), 40);

		if (prefersReducedMotion() || start === score) {
			setDisplayValue(score);
			return () => clearTimeout(ringTimer);
		}

		let raf = 0;
		let done = false;
		const duration = 800;
		const t0 = performance.now();
		const tick = (now) => {
			const t = Math.min(1, (now - t0) / duration);
			const eased = 1 - Math.pow(1 - t, 3);
			setDisplayValue(Math.round(start + (score - start) * eased));
			if (t < 1) raf = requestAnimationFrame(tick);
			else done = true;
		};
		raf = requestAnimationFrame(tick);
		const numberFallback = setTimeout(() => {
			if (!done) setDisplayValue(score);
		}, duration + 150);

		return () => {
			clearTimeout(ringTimer);
			clearTimeout(numberFallback);
			cancelAnimationFrame(raf);
		};
	}, [score, animate]);

	// Non-Overview headers skip the load animation — render the ring + number at their
	// final values immediately (no count-up, no ring transition).
	const ringFraction = animate ? ringPct : score != null ? score / 100 : 0;
	const shownValue = animate ? displayValue : (score ?? 0);
	const offset = c - Math.min(Math.max(ringFraction, 0), 1) * c;
	const targetAngle = -Math.PI / 2 + 2 * Math.PI * (TARGET_SCORE / 100);
	const tickInner = r - stroke / 2 - 1;
	const tickOuter = r + stroke / 2 + 1;
	const cx = size / 2;
	const cy = size / 2;
	const tx1 = cx + Math.cos(targetAngle) * tickInner;
	const ty1 = cy + Math.sin(targetAngle) * tickInner;
	const tx2 = cx + Math.cos(targetAngle) * tickOuter;
	const ty2 = cy + Math.sin(targetAngle) * tickOuter;

	return (
		<ComingSoonOverlay active={comingSoon} title="Local Potential Score" icon={Gauge} badgeText="Coming soon">
			<div
				data-tour="local-potential"
				className={`relative w-full rounded-2xl border border-border/60 bg-gradient-soft shadow-[0_1px_2px_rgba(15,23,42,0.04)] md:w-auto ${compact ? 'px-3 py-2' : 'p-3.5 md:p-4'}`}
			>
				<div className={`flex items-center ${compact ? 'gap-3' : 'gap-4'}`}>
					<div className={`order-1 min-w-0 flex-1 ${compact ? 'md:w-auto' : 'md:w-[260px]'}`}>
						<div className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
							<Gauge className="h-3.5 w-3.5 text-primary" />
							Local Potential Score
						</div>
						{!compact && <p className="mt-1.5 text-[12px] leading-snug text-muted-foreground">{subtitle}</p>}
						<div className={compact ? 'mt-1.5' : 'mt-2.5'}>
							<Link
								to={shopPath('/local-potential')}
								className="inline-flex items-center gap-1.5 rounded-full bg-gradient-unify px-3 py-1.5 text-[12px] font-semibold text-white shadow-sm transition hover:opacity-90"
							>
								Improve my store <ArrowRight className="h-3 w-3" />
							</Link>
						</div>
					</div>

					<div className="relative order-2 shrink-0" style={{ width: size, height: size }}>
						{/* Solid inner disc for contrast against the soft gradient pill background */}
						<div
							className="absolute rounded-full bg-white shadow-[0_1px_3px_rgba(15,23,42,0.08)]"
							style={{ inset: stroke + 1 }}
							aria-hidden
						/>
						<svg width={size} height={size} className="relative">
							<g transform={`rotate(-90 ${cx} ${cy})`}>
								<circle cx={cx} cy={cy} r={r} stroke="hsl(var(--border))" strokeWidth={stroke} fill="none" />
								{!loading && (
									<circle
										cx={cx}
										cy={cy}
										r={r}
										stroke="url(#headerGrad)"
										strokeWidth={stroke}
										strokeLinecap="round"
										fill="none"
										strokeDasharray={c}
										strokeDashoffset={offset}
										style={
											animate ? { transition: 'stroke-dashoffset 800ms cubic-bezier(0.22, 1, 0.36, 1)' } : undefined
										}
									/>
								)}
							</g>
							{!loading && (
								<line
									x1={tx1}
									y1={ty1}
									x2={tx2}
									y2={ty2}
									stroke="hsl(var(--foreground))"
									strokeWidth={1.5}
									strokeLinecap="round"
								>
									<title>Target: {TARGET_SCORE}%</title>
								</line>
							)}
							<defs>
								<linearGradient id="headerGrad" x1="0" y1="0" x2="1" y2="1">
									<stop offset="0%" stopColor="hsl(var(--primary))" />
									<stop offset="100%" stopColor="hsl(var(--action))" />
								</linearGradient>
							</defs>
						</svg>
						<div className="absolute inset-0 flex items-center justify-center">
							{loading ? (
								<Loader2 className="h-4 w-4 animate-spin text-primary/70" aria-label="Loading your score" />
							) : (
								<span className="text-base font-extrabold text-foreground">{shownValue}%</span>
							)}
						</div>
					</div>
				</div>
			</div>
		</ComingSoonOverlay>
	);
}

/**
 * Zero-config compact score for page headers — self-fetches the live score.
 */
export function PageScore() {
	return <LocalPotentialHeader compact />;
}
